// @spec SHL-STR-020
// @spec SHL-STR-021
// @spec SHL-STR-049
// Centralized command registry for the command palette

import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  Building,
  FileText,
  MonitorSmartphone,
  Moon,
  Palette,
  Plus,
  Sun,
  User,
  Users,
} from 'lucide-react';

import { getNavigationConfig } from '@/components/sidebar/navigation-config';

/**
 * Command category for grouping commands in the palette.
 */
export type CommandCategory = 'navigation' | 'actions' | 'settings' | 'recent';

/**
 * Display labels for command categories.
 */
export const COMMAND_CATEGORY_LABELS: Record<CommandCategory, string> = {
  navigation: 'Navigation',
  actions: 'Actions',
  settings: 'Settings',
  recent: 'Recent',
};

/**
 * Order in which categories should be displayed in the command palette.
 */
export const COMMAND_CATEGORY_ORDER: CommandCategory[] = [
  'recent',
  'navigation',
  'actions',
  'settings',
];

/**
 * Single command configuration in the palette.
 */
export interface AppCommand {
  /** Unique identifier */
  id: string;
  /** Display label */
  label: string;
  /** Icon component from lucide-react */
  icon: LucideIcon;
  /** Command category for grouping */
  category: CommandCategory;
  /** Keywords for fuzzy search (in addition to label) */
  keywords?: string[];
  /** Keyboard shortcut display (if registered) */
  shortcut?: string;
  /** Action to execute - returns route path for navigation or undefined for actions */
  action: () => string | undefined | Promise<string | undefined>;
  /** Context where command is available (route pattern) - undefined = global */
  context?: string | RegExp;
  /** Whether command is currently disabled */
  disabled?: boolean;
}

/**
 * Generate navigation commands from the navigation config.
 * Creates "Go to X" commands for each navigation item.
 */
function generateNavigationCommands(): AppCommand[] {
  const commands: AppCommand[] = [];

  for (const group of getNavigationConfig()) {
    for (const item of group.items) {
      commands.push({
        id: `go-${item.id}`,
        label: `Go to ${item.label}`,
        icon: item.icon,
        category: 'navigation',
        keywords: [item.label.toLowerCase(), group.label.toLowerCase()],
        action: () => item.to,
      });
    }
  }

  return commands;
}

/**
 * Static action commands for creating entities.
 * Context-aware: only shown when on relevant pages.
 */
const ACTION_COMMANDS: AppCommand[] = [
  {
    id: 'create-task',
    label: 'Create New Task',
    icon: Plus,
    category: 'actions',
    keywords: ['new', 'add', 'task', 'create'],
    context: /^\/tasks/,
    action: () => {
      // Will integrate with task creation modal when available
      // For now, navigate to tasks page
      return '/tasks';
    },
  },
  {
    id: 'create-risk',
    label: 'Create New Risk',
    icon: AlertTriangle,
    category: 'actions',
    keywords: ['new', 'add', 'risk', 'create'],
    context: /^\/risks/,
    action: () => {
      // Will integrate with risk creation modal when available
      return '/risks';
    },
  },
  {
    id: 'create-document',
    label: 'Create New Document',
    icon: FileText,
    category: 'actions',
    keywords: ['new', 'add', 'document', 'create', 'file'],
    context: /^\/documents/,
    action: () => {
      // Will integrate with document upload/creation when available
      return '/documents';
    },
  },
];

/**
 * Custom event name for theme changes from command palette.
 * ThemeProvider listens for this event and updates the theme accordingly.
 */
export const THEME_CHANGE_EVENT = 'quantifier:set-theme';

/**
 * Settings commands for toggling application preferences.
 * Theme commands dispatch custom events that ThemeProvider listens to.
 */
const SETTINGS_COMMANDS: AppCommand[] = [
  {
    id: 'set-light-mode',
    label: 'Set Light Theme',
    icon: Sun,
    category: 'settings',
    keywords: ['theme', 'light', 'appearance', 'mode'],
    action: () => {
      window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: 'light' }));
      return undefined;
    },
  },
  {
    id: 'set-dark-mode',
    label: 'Set Dark Theme',
    icon: Moon,
    category: 'settings',
    keywords: ['theme', 'dark', 'appearance', 'mode'],
    action: () => {
      window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: 'dark' }));
      return undefined;
    },
  },
  {
    id: 'set-system-mode',
    label: 'Use System Theme',
    icon: MonitorSmartphone,
    category: 'settings',
    keywords: ['theme', 'system', 'appearance', 'mode', 'auto'],
    action: () => {
      window.dispatchEvent(new CustomEvent(THEME_CHANGE_EVENT, { detail: 'system' }));
      return undefined;
    },
  },
  {
    id: 'go-appearance-settings',
    label: 'Appearance Settings',
    icon: Palette,
    category: 'settings',
    keywords: ['theme', 'dark', 'light', 'appearance', 'display'],
    action: () => '/settings/appearance',
  },
  {
    id: 'go-profile-settings',
    label: 'Profile Settings',
    icon: User,
    category: 'settings',
    keywords: ['account', 'profile', 'preferences'],
    action: () => '/settings/profile',
  },
  {
    id: 'go-organization-settings',
    label: 'Organisation Structure',
    icon: Building,
    category: 'settings',
    keywords: ['org', 'company', 'workspace', 'departments', 'facilities', 'subsidiaries'],
    action: () => '/settings/organisation-structure',
  },
  {
    id: 'go-team-settings',
    label: 'Team Settings',
    icon: Users,
    category: 'settings',
    keywords: ['members', 'users', 'team'],
    action: () => '/settings/team',
  },
];

/**
 * Combined command registry with all available commands.
 */
export const COMMAND_REGISTRY: AppCommand[] = [
  ...generateNavigationCommands(),
  ...ACTION_COMMANDS,
  ...SETTINGS_COMMANDS,
];

/**
 * Get a command by its ID.
 */
export function getCommandById(id: string): AppCommand | undefined {
  return COMMAND_REGISTRY.find((cmd) => cmd.id === id);
}

/**
 * Get commands filtered by category.
 */
export function getCommandsByCategory(category: CommandCategory): AppCommand[] {
  return COMMAND_REGISTRY.filter((cmd) => cmd.category === category);
}

/**
 * Get commands grouped by category.
 */
export function getCommandsGroupedByCategory(): Map<CommandCategory, AppCommand[]> {
  const grouped = new Map<CommandCategory, AppCommand[]>();

  for (const command of COMMAND_REGISTRY) {
    const existing = grouped.get(command.category) ?? [];
    grouped.set(command.category, [...existing, command]);
  }

  return grouped;
}

/**
 * Filter commands based on search query.
 * Matches against label and keywords (case-insensitive).
 */
export function filterCommands(commands: AppCommand[], query: string): AppCommand[] {
  if (!query.trim()) {
    return commands;
  }

  const searchTerms = query.toLowerCase().split(/\s+/).filter(Boolean);

  return commands.filter((command) => {
    const searchableText = [command.label.toLowerCase(), ...(command.keywords ?? [])].join(' ');

    return searchTerms.every((term) => searchableText.includes(term));
  });
}

/**
 * Check if a command is available in the current context.
 */
export function isCommandAvailable(command: AppCommand, currentPath: string): boolean {
  if (command.disabled) {
    return false;
  }

  if (!command.context) {
    return true;
  }

  if (typeof command.context === 'string') {
    return currentPath.startsWith(command.context);
  }

  return command.context.test(currentPath);
}
